import { Box, Stack, Typography } from '@mui/material';
import { Banner, Spinner } from '@shopify/polaris';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getAppReleaseQrLink } from 'src/api/app_release_api';
import { Badge } from 'src/components/badge/badge';
import StackedModal from 'src/components/dialog/stacked_modal';
import { getErrorMessage } from 'src/utils/error_message';

const QR_SIZE = 216;
const PROD_ENVIRONMENT = 'Prod';

const formatRemaining = (seconds) => {
  const safe = Math.max(0, seconds);
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
};

const AppInstallModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['app_release']);
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const loadLink = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAppReleaseQrLink();
      setLink(res?.result ?? null);
      setRemaining(res?.result?.expiresInSeconds ?? 0);
    } catch (err) {
      setLink(null);
      setRemaining(0);
      setError(
        err?.response?.status === 404 ? t('app_release:message.no_release') : getErrorMessage(err),
      );
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (isOpen) loadLink();
  }, [isOpen, loadLink]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setInterval(() => setRemaining((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  const isExpired = Boolean(link) && remaining <= 0;
  const isDev = Boolean(link) && link.environment !== PROD_ENVIRONMENT;

  const renderBody = () => {
    if (loading && !link) {
      return (
        <Stack
          alignItems={'center'}
          sx={{ py: 6 }}
        >
          <Spinner size="large" />
        </Stack>
      );
    }

    if (error) {
      return (
        <Stack
          alignItems={'center'}
          spacing={2}
          sx={{ py: 5 }}
        >
          <Typography variant={'body_md'}>{error}</Typography>
        </Stack>
      );
    }

    if (!link) return null;

    return (
      <Stack
        alignItems={'center'}
        spacing={2}
      >
        <Box
          sx={{
            width: '100%',
            '& .Polaris-InlineStack': { alignItems: 'center !important' },
          }}
        >
          <Banner tone="info">
            <Stack spacing={0.5}>
              <Typography variant={'body_md'}>{t('app_release:message.scan_guide')}</Typography>
              <Typography variant={'body_md'}>
                {t('app_release:message.unknown_sources')}
              </Typography>
            </Stack>
          </Banner>
        </Box>

        <Box
          sx={{
            position: 'relative',
            padding: 'var(--p-space-300)',
            border: 'var(--p-border-width-025) solid var(--p-color-border)',
            borderRadius: 'var(--p-border-radius-300)',
            backgroundColor: 'var(--p-color-bg-surface)',
          }}
        >
          <QRCodeSVG
            value={link.downloadUrl}
            size={QR_SIZE}
            level="M"
          />
          {isExpired && (
            <Stack
              alignItems={'center'}
              justifyContent={'center'}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'var(--p-color-bg-surface)',
                opacity: 0.94,
                borderRadius: 'var(--p-border-radius-300)',
              }}
            >
              <Typography variant={'body_md_semibold'}>
                {t('app_release:message.expired')}
              </Typography>
            </Stack>
          )}
        </Box>

        <Typography
          variant={'body_sm'}
          sx={{ color: 'text.secondary' }}
        >
          {isExpired ? (
            t('app_release:message.refresh_hint')
          ) : (
            <Trans
              i18nKey="app_release:message.expires_in"
              values={{ remaining: formatRemaining(remaining) }}
              components={{
                bold: (
                  <Box
                    component="span"
                    sx={{ fontWeight: 650 }}
                  />
                ),
              }}
            />
          )}
        </Typography>

        <Box
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            padding: 'var(--p-space-300)',
            borderRadius: 'var(--p-border-radius-300)',
            backgroundColor: 'var(--p-color-bg-surface-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--p-space-300)',
          }}
        >
          <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant={'body_md_semibold'}>
              {t('app_release:install_version', { version: link.versionName })}
            </Typography>
          </Stack>
          {isDev && <Badge tone="warning">{t('app_release:env_test')}</Badge>}
        </Box>
      </Stack>
    );
  };

  return (
    <StackedModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('app_release:install_app')}
      content={renderBody()}
      sx={{ width: { xs: '450px', sm: '520px', md: '520px' } }}
      primaryAction={{
        content: t('app_release:refresh'),
        onAction: loadLink,
        loading: loading,
      }}
    />
  );
};

export default AppInstallModal;
